import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, styled, Tabs, tabsClasses } from '@mui/material';
import { useCompanyRelativePath } from 'ogp/hooks';
import { ServiceAgreementCompany } from 'ogp/services/service-agreements.schema';
import { Header, LwTab, Page } from 'redesign';
import { routes } from 'Routes';
import { RenderUnsettledUI } from 'shared/components';
import { invariant } from 'shared/utils/utils';
import { CompanyDetailToolbox } from './company-detail-toolbox';
import { CompanyDetails } from './company-details';
import { CompanyUsers } from './company-users';
import { ServiceAgreementTab } from './service-agreement-tab';
import { CompanyDetailTab, useCompanyDetailTabs } from './use-company-detail-tabs';
import { useGetServiceAgreementCompany } from '../../hooks/use-get-service-agreement-company';

export const CompanyDetailDataProvider = () => {
  const { id } = useParams<'id'>();
  invariant(id);
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const query = useGetServiceAgreementCompany(id);

  if (query.status !== 'success') {
    return <RenderUnsettledUI data={query} />;
  }

  return (
    <CompanyDetail
      data={query.data}
      onBack={() => navigate(generatePath(routes.CollegialLendingCustomers))}
    />
  );
};

const CompanyDetail = ({ data, onBack }: { data: ServiceAgreementCompany; onBack: () => void }) => {
  const { t } = useTranslation('ogp-screen-specific');
  const { selectedTab, selectTab, tabs } = useCompanyDetailTabs();

  return (
    <Page
      header={<Header titleText={data.company.name} />}
      toolbox={<CompanyDetailToolbox onBack={onBack} />}
    >
      <Box width="100%">
        <StyledTabs
          value={selectedTab}
          onChange={(_, tabId: CompanyDetailTab) => selectTab(tabId)}
          aria-label={t('collegial-lending.customers.detail.tabs.aria-label', {
            defaultValue: 'Klantdetails',
          })}
        >
          {tabs.map((tab) => (
            <LwTab key={tab.id} value={tab.id} label={tab.label} />
          ))}
        </StyledTabs>

        <Box mt={4}>
          {selectedTab === 'details' ? <CompanyDetails company={data.company} /> : null}
          {selectedTab === 'users' ? <CompanyUsers users={data.users} /> : null}
          {selectedTab === 'service-agreement' ? (
            <ServiceAgreementTab serviceAgreement={data.serviceAgreement} />
          ) : null}
        </Box>
      </Box>
    </Page>
  );
};

const StyledTabs = styled(Tabs)(({ theme }) => ({
  [`& div.${tabsClasses.scroller}`]: {
    [`& .${tabsClasses.flexContainer}`]: {
      gap: theme.spacing(2),
      overflow: 'visible',
    },
  },
}));
